import MarketingLayout from "@/components/MarketingLayout";
import { Heart, Globe, Users } from "lucide-react";

const MISSION_IMG = "https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=768,h=841,fit=crop/YbNar5pQPVHKQvVK/imagejpeg_0-AQEpN6Ll95C93avn.jpg";
const STORY_IMG = "/story-image.jpg";

const values = [
  { icon: Users, title: "Community", description: "Empowering students and volunteers to create meaningful impact together.", color: "bg-[#D6E6FA]" },
  { icon: Globe, title: "Access", description: "Making quality language education available to everyone.", color: "bg-[#FBE7C6]" },
  { icon: Heart, title: "Connection", description: "Building friendships, cultural understanding, and global perspectives.", color: "bg-[#FFAEBC]" },
];

const AboutPage = () => {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="py-20 sm:py-28 px-6 bg-gradient-to-b from-[#D6E6FA]/40 to-[#FDFBF7]">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-center lg:text-left max-w-xl mx-auto lg:mx-0">
              <div className="inline-block bg-[#3C8DE4] text-white border-2 border-[#0A0A0A] rounded-full px-4 py-1 text-xs font-bold uppercase tracking-wider mb-6" data-testid="about-badge">
                About Us
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
                Our Mission & Vision
              </h1>
              <p className="text-base sm:text-lg text-[#4A4A4A] leading-relaxed">
                At Virtually Verbal, we believe that language learning is more than mastering vocabulary and grammar; it is a gateway to understanding people, cultures, and perspectives from around the world.
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="brutalist-card overflow-hidden">
                <img src={MISSION_IMG} alt="Virtually Verbal community" className="w-full h-96 object-cover" loading="eager" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 px-6">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Mission */}
            <div className="brutalist-card p-8" data-testid="mission-card">
              <div className="w-12 h-12 rounded-full bg-[#3C8DE4] border-2 border-[#0A0A0A] flex items-center justify-center mb-5">
                <Globe className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <h2 className="text-base sm:text-lg font-bold mb-4">Our Mission</h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                Our mission is to make language education accessible to everyone by connecting learners with passionate volunteer tutors through free online programs. By removing financial and geographic barriers to education, we create opportunities for students to develop language skills, build confidence, and engage in meaningful cultural exchange. Through every lesson, conversation, and connection, we strive to foster a more inclusive and globally connected community.
              </p>
            </div>

            {/* Vision */}
            <div className="brutalist-card p-8" data-testid="vision-card">
              <div className="w-12 h-12 rounded-full bg-[#FFAEBC] border-2 border-[#0A0A0A] flex items-center justify-center mb-5">
                <Heart className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <h2 className="text-base sm:text-lg font-bold mb-4">Our Vision</h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                We envision a world where language learning is accessible to all, regardless of background, location, or financial circumstances. By bringing together learners and volunteers from diverse communities, we aim to promote cross-cultural understanding, lifelong learning, and global citizenship. Through education and connection, we hope to empower individuals to communicate across borders and contribute to a more connected world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 px-6 bg-white border-y-2 border-[#0A0A0A]">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <div>
              <div className="inline-block bg-[#FBE7C6] border-2 border-[#0A0A0A] rounded-full px-4 py-1 text-xs font-bold uppercase tracking-wider mb-6">
                Our Story
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">How It All Started</h2>
              <div className="space-y-4 text-sm text-[#4A4A4A] leading-relaxed">
                <p>
                  Virtually Verbal began during the COVID-19 pandemic when founder Aarya Gokhale started teaching languages to family members in India. What began as a small effort to stay connected through learning quickly revealed the power of virtual education to bring people together across continents.
                </p>
                <p>
                  As interest grew, Aarya partnered with co-founder Tanishka Padhye to expand the initiative beyond family and friends. Together, they recruited volunteers, developed structured language programs, and welcomed students from diverse backgrounds.
                </p>
                <p>
                  Today, Virtually Verbal is a student-led nonprofit organization that connects learners and volunteers across multiple countries through free online language education. Our programs provide more than language instruction—they create opportunities for cultural exchange, mentorship, friendship, and personal growth.
                </p>
              </div>
            </div>
            <div className="relative flex flex-col">
              <div className="brutalist-card overflow-hidden flex-1 relative min-h-[400px]" data-testid="story-image">
                <img src={STORY_IMG} alt="Virtually Verbal team" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="brutalist-card p-5 bg-[#FBE7C6] -mt-8 ml-6 mr-0 sm:-mt-12 sm:ml-12 relative z-10 max-w-sm">
                <p className="text-sm font-semibold italic">
                  &ldquo;We started Virtually Verbal with a simple goal: making language learning accessible to anyone, anywhere.&rdquo;
                </p>
                <p className="text-xs font-bold mt-2 text-[#4A4A4A]">— Aarya & Tanishka</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-6" data-testid="values-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Our Values</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {values.map((v) => (
              <div key={v.title} className={`brutalist-card p-8 text-center ${v.color}`} data-testid={`value-${v.title.toLowerCase()}`}>
                <div className="w-14 h-14 rounded-full bg-white border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-5">
                  <v.icon className="w-7 h-7" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold mb-2">{v.title}</h3>
                <p className="text-sm text-[#4A4A4A]">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
};

export default AboutPage;
